import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Check,
  Mail,
  Phone,
  Search,
  Sparkles,
  Star,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "../components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";

import EmailGate from "../components/EmailGate";
import {
  buildContentTrackingMetadata,
  getStoredContentSessionId,
} from "../lib/gatedContentTracking";
import AgentTitleSlide from "../components/slides/AgentTitleSlide";
import AIReceptionistSlide from "../components/slides/AIReceptionistSlide";
import AlainaSlide from "../components/slides/AlainaSlide";
import ArielSlide from "../components/slides/ArielSlide";
import AshaVsDentinaReceptionistSlide from "../components/slides/AshaVsDentinaReceptionistSlide";
import AshaVsDentinaSlide from "../components/slides/AshaVsDentinaSlide";
import AshaVsWeaveSlide from "../components/slides/AshaVsWeaveSlide";
import BackingSlide from "../components/slides/BackingSlide";
import BackOfficeCompetitorAnalysisSlide from "../components/slides/BackOfficeCompetitorAnalysisSlide";
import AshaOperatingSystemSlide from "../components/slides/AshaOperatingSystemSlide";
import TotalPricingComparisonSlide from "../components/slides/TotalPricingComparisonSlide";
import BenefitFormsSlide from "../components/slides/BenefitFormsSlide";
import CaseStudySlide from "../components/slides/CaseStudySlide";
import CommunicationSuiteSlide from "../components/slides/CommunicationSuiteSlide";
import MarketingTitleSlide from "../components/slides/MarketingTitleSlide";
import SMSCampaignsSlide from "../components/slides/SMSCampaignsSlide";
import EmailCampaignsSlide from "../components/slides/EmailCampaignsSlide";
import CommunicationTitleSlide from "../components/slides/CommunicationTitleSlide";
import ComparisonsTitleSlide from "../components/slides/ComparisonsTitleSlide";
import AshaVsLassieSlide from "../components/slides/AshaVsLassieSlide";
import CompetitorAnalysisSlide from "../components/slides/CompetitorAnalysisSlide";
import CommunicationSuiteComparisonSlide from "../components/slides/CommunicationSuiteComparisonSlide";
import ComprehensiveBreakdownsSlide from "../components/slides/ComprehensiveBreakdownsSlide";
import EFAXSlide from "../components/slides/EFAXSlide";
import ElisePatientExperienceSlide from "../components/slides/ElisePatientExperienceSlide";
import MorganPatientIntakeSlide from "../components/slides/MorganPatientIntakeSlide";
import PracticeManagerSlide from "../components/slides/PracticeManagerSlide";
import FrontOfficeGettingStartedSlide from "../components/slides/FrontOfficeGettingStartedSlide";
import OnboardingTimelineSlide from "../components/slides/OnboardingTimelineSlide";
import GettingStartedSlide from "../components/slides/GettingStartedSlide";
import ContractTermsSlide from "../components/slides/ContractTermsSlide";
import WhiteGloveOnboardingSlide from "../components/slides/WhiteGloveOnboardingSlide";
import FAQSlide from "../components/slides/FAQSlide";
import HumanBillerSlide from "../components/slides/HumanBillerSlide";
import HygieneRecallSlide from "../components/slides/HygieneRecallSlide";
import InsuranceCallingSlide from "../components/slides/InsuranceCallingSlide";
import InsuranceCopilotSlide from "../components/slides/InsuranceCopilotSlide";
import InsuranceVerificationSlide from "../components/slides/InsuranceVerificationSlide";
import IntelligentSchedulingSlide from "../components/slides/IntelligentSchedulingSlide";
import KimCaseAcceptanceSlide from "../components/slides/KimCaseAcceptanceSlide";
import LenaSlide from "../components/slides/LenaSlide";
import LenaWorkflowSlide from "../components/slides/LenaWorkflowSlide";
import NextGenFormsSlide from "../components/slides/NextGenFormsSlide";
import RealROISlide from "../components/slides/RealROISlide";
import ROIIntroSlide from "../components/slides/ROIIntroSlide";
import VoIPSlide from "../components/slides/VoIPSlide";

import EOBProcessingSlide from "../components/slides/EOBProcessingSlide";
import EOBStreamliningSlide from "../components/slides/EOBStreamliningSlide";
import PatientBillingSlide from "../components/slides/PatientBillingSlide";
import PaymentReconciliationSlide from "../components/slides/PaymentReconciliationSlide";

import OfferingsSlide from "../components/slides/OfferingsSlide";

// Updated slides array to include title slides and proper indentation
const slides = [
  { id: 0, slug: "welcome", title: "Welcome", indent: 0 },
  { id: 1, slug: "why-asha", title: "Why Asha?", indent: 0 },
  { id: 2, slug: "backing", title: "Backing & Team", indent: 0 },
  { id: 99, slug: "offerings", title: "What We Offer", indent: 0 },
  { id: 3, slug: "front-office", title: "Front Office Team", indent: 0 },
  { id: 4, slug: "amy", title: "Amy, Receptionist", indent: 1 },
  { id: 5, slug: "amy-features", title: "Features", indent: 2 },
  { id: 6, slug: "amy-scheduling", title: "Intelligent Scheduling", indent: 2 },
  { id: 7, slug: "leena", title: "Leena, Schedule Optimizer", indent: 1 },
  { id: 8, slug: "leena-features", title: "Features", indent: 2 },
  { id: 9, slug: "leena-workflow", title: "12-Step Workflow", indent: 2 },
  { id: 10, slug: "stephanie", title: "Stephanie, Hygiene Recall", indent: 1 },
  { id: 11, slug: "stephanie-features", title: "Features", indent: 2 },
  { id: 110, slug: "kim", title: "Kim, Case Acceptance", indent: 1 },
  { id: 111, slug: "kim-revenue", title: "Revenue Automation", indent: 2 },
  { id: 112, slug: "elise", title: "Elise, Patient Experience", indent: 1 },
  { id: 113, slug: "elise-reviews", title: "Review Generation", indent: 2 },
  { id: 114, slug: "morgan", title: "Morgan, Patient Intake", indent: 1 },
  { id: 115, slug: "morgan-intake", title: "Intake & Insurance", indent: 2 },
  { id: 116, slug: "practice-manager", title: "Trisha, Practice Manager", indent: 1 },
  { id: 117, slug: "practice-manager-dashboard", title: "Task Dashboard", indent: 2 },
  { id: 17, slug: "comms-suite", title: "Communication Suite", indent: 1 },
  { id: 18, slug: "voip", title: "VoIP", indent: 2 },
  { id: 29, slug: "forms", title: "Next-Gen Forms", indent: 2 },
  { id: 19, slug: "messaging", title: "2-Way Messaging", indent: 2 },
  { id: 20, slug: "fax", title: "Intelligent Fax", indent: 2 },
  { id: 201, slug: "sms-campaigns", title: "SMS Campaigns", indent: 2 },
  { id: 202, slug: "email-campaigns", title: "Email Campaigns", indent: 2 },
  { id: 22, slug: "back-office", title: "Back Office Team", indent: 0 },
  {
    id: 12,
    slug: "amanda",
    title: "Amanda, Insurance Verification",
    indent: 1,
  },
  { id: 13, slug: "amanda-checks", title: "Automatic Checks", indent: 2 },
  {
    id: 30,
    slug: "amanda-breakdowns",
    title: "Comprehensive Breakdowns",
    indent: 2,
  },
  { id: 31, slug: "amanda-copilot", title: "AI Insurance Co-Pilot", indent: 2 },
  { id: 16, slug: "amanda-forms", title: "Automated Forms", indent: 2 },
  { id: 15, slug: "amanda-calling", title: "AI Calling", indent: 2 },
  { id: 23, slug: "ariel", title: "Ariel, Claims Expert", indent: 1 },
  { id: 24, slug: "ariel-submission", title: "Claim Submission", indent: 2 },
  { id: 101, slug: "ariel-scrubbing", title: "Claim Scrubbing", indent: 2 },
  { id: 100, slug: "alisha", title: "Alisha, EOB Posting", indent: 1 },
  { id: 25, slug: "alisha-posting", title: "Automated Posting", indent: 2 },
  {
    id: 102,
    slug: "alisha-reconciliation",
    title: "Payment Reconciliation",
    indent: 2,
  },
  { id: 103, slug: "alisha-ingestion", title: "Unified Ingestion", indent: 2 },
  { id: 26, slug: "kianna", title: "Kianna, Patient Billing", indent: 1 },
  { id: 104, slug: "kianna-campaigns", title: "Voice Campaigns", indent: 2 },
  { id: 27, slug: "human-biller", title: "Human Biller", indent: 1 },
  { id: 150, slug: "pricing", title: "Pricing & Packaging", indent: 0 },
  {
    id: 32,
    slug: "front-office-competitors",
    title: "Front Office Competitor Pricing Comparison",
    indent: 1,
  },
  {
    id: 33,
    slug: "communication-suite-comparison",
    title: "Communication + Marketing Suite",
    indent: 1,
  },
  {
    id: 107,
    slug: "back-office-competitors",
    title: "Back Office Competitor Comparison",
    indent: 1,
  },
  {
    id: 151,
    slug: "total-pricing-comparison",
    title: "Total Pricing Comparison",
    indent: 1,
  },
  {
    id: 203,
    slug: "asha-operating-system",
    title: "Asha AI Operating System",
    indent: 1,
  },
  { id: 210, slug: "getting-started", title: "Getting Started", indent: 0 },
  {
    id: 28,
    slug: "front-office-start",
    title: "Getting Started (Front Office)",
    indent: 1,
  },
  {
    id: 105,
    slug: "back-office-start",
    title: "Getting Started (Back Office)",
    indent: 1,
  },
  {
    id: 109,
    slug: "white-glove-onboarding",
    title: "White Glove Onboarding",
    indent: 1,
  },
  {
    id: 155,
    slug: "contract-terms",
    title: "Contract Terms & Guarantees",
    indent: 1,
  },
  { id: 215, slug: "faq", title: "FAQ", indent: 0 },
  {
    id: 216,
    slug: "frequently-asked-questions",
    title: "Frequently Asked Questions",
    indent: 1,
  },
  { id: 120, slug: "roi", title: "ROI & Impact", indent: 0 },
  {
    id: 122,
    slug: "case-study-ntx",
    title: "NTX Mental Health Case Study",
    indent: 1,
  },
  {
    id: 124,
    slug: "case-study-procare",
    title: "Procare Case Study",
    indent: 1,
  },
  {
    id: 125,
    slug: "case-study-advanced-dental",
    title: "Advanced Dental Case Study",
    indent: 1,
  },
  {
    id: 126,
    slug: "case-study-rowland",
    title: "Rowland Family Case Study",
    indent: 1,
  },
  /*
  {
    id: 127,
    slug: "case-study-alpha",
    title: "Alpha Dental Case Study",
    indent: 1,
  },
  */
  { id: 123, slug: "roi-data", title: "Real Practice Data", indent: 1 },
  { id: 130, slug: "comparisons", title: "Comparisons", indent: 0 },
  {
    id: 131,
    slug: "vs-dentina-platform",
    title: "Asha vs Dentina: Platform",
    indent: 1,
  },
  {
    id: 133,
    slug: "vs-dentina-receptionist",
    title: "Asha vs Dentina: Receptionist",
    indent: 1,
  },
  { id: 132, slug: "vs-weave", title: "Asha vs Weave", indent: 1 },
  { id: 134, slug: "vs-lassie", title: "Asha vs Lassie", indent: 1 },
];

const SlideshowPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [scale, setScale] = useState(1);
  const [isLocked, setIsLocked] = useState(true);
  const containerRef = React.useRef(null);
  const slideStartTimeRef = React.useRef(Date.now());
  const currentSlideRef = React.useRef(0);

  // Track slide views (for backend)
  const recordSlideView = (slideIndex, duration, useBeacon = false) => {
    const sessionId = getStoredContentSessionId("asha_slideshow_session_id");
    if (!sessionId || duration < 1000) return;

    const slide = slides[slideIndex];
    const payload = JSON.stringify({
      sessionId,
      slideId: slide?.id ?? slideIndex,
      slideTitle: slide?.title ?? `Slide ${slideIndex}`,
      slideSlug: slide?.slug,
      durationSeconds: Math.round(duration / 1000),
      ...buildContentTrackingMetadata({ contentType: "slideshow" }),
    });

    const isLocalhost =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1");
    const apiUrl =
      process.env.REACT_APP_API_URL ||
      (isLocalhost ? "http://localhost:8001" : "");
    const url = `${apiUrl}/api/slideshow/slide-view`;

    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }

    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch((err) => console.error("Failed to record slide view", err));
  };

  useEffect(() => {
    // Check if user has already granted access
    const hasAccess = localStorage.getItem("asha_slideshow_access");
    const sessionId = getStoredContentSessionId("asha_slideshow_session_id");
    if (hasAccess && sessionId) {
      setIsLocked(false);
    }
  }, []);

  const handleAccessGranted = (email) => {
    localStorage.setItem("asha_slideshow_access", "true");
    // Optional: You could save the email to a backend here
    console.log("Access granted for:", email);
    currentSlideRef.current = api?.selectedScrollSnap?.() ?? 0;
    slideStartTimeRef.current = Date.now();
    setIsLocked(false);
  };

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const parentWidth = containerRef.current.offsetWidth;
        // Desktop design width
        const targetWidth = 1400;
        const newScale = parentWidth / targetWidth;
        setScale(newScale);
      }
    };

    // Initial calculation
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      const newIndex = api.selectedScrollSnap();

      // Calculate duration for previous slide (for backend tracking)
      const now = Date.now();
      const duration = now - slideStartTimeRef.current;

      // Record previous slide
      recordSlideView(currentSlideRef.current, duration);

      // Update refs for new slide
      currentSlideRef.current = newIndex;
      slideStartTimeRef.current = now;

      setCurrent(newIndex + 1);

      // Update URL with slide slug
      const slideSlug = slides[newIndex]?.slug;
      if (slideSlug) {
        setSearchParams({ slide: slideSlug }, { replace: true });
      }
    });

    // Cleanup/Unmount - record final slide
    return () => {
      const now = Date.now();
      const duration = now - slideStartTimeRef.current;
      recordSlideView(currentSlideRef.current, duration);
    };
  }, [api, setSearchParams]);

  useEffect(() => {
    const flushOnHide = () => {
      const now = Date.now();
      const duration = now - slideStartTimeRef.current;
      recordSlideView(currentSlideRef.current, duration, true);
      slideStartTimeRef.current = now;
    };
    window.addEventListener("pagehide", flushOnHide);
    return () => window.removeEventListener("pagehide", flushOnHide);
  }, []);

  useEffect(() => {
    if (!api) return;

    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight") {
        api.scrollNext();
      } else if (event.key === "ArrowLeft") {
        api.scrollPrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [api]);

  // Navigate to slide from URL query parameter
  useEffect(() => {
    if (!api) return;

    const slideParam = searchParams.get("slide");
    if (slideParam) {
      // Try to find slide by slug first
      let slideIndex = slides.findIndex((s) => s.slug === slideParam);

      // Fallback: support old numeric IDs for backward compatibility
      if (slideIndex === -1) {
        const slideId = parseInt(slideParam, 10);
        if (!isNaN(slideId)) {
          slideIndex = slides.findIndex((s) => s.id === slideId);
        }
      }

      if (slideIndex !== -1) {
        // Small delay to ensure carousel is ready
        setTimeout(() => {
          api.scrollTo(slideIndex);
        }, 100);
      }
    }
  }, [api, searchParams]);

  const filteredSlides = slides
    .map((slide, index) => ({ ...slide, originalIndex: index }))
    .filter((slide) =>
      slide.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

  return (
    <div className="min-h-screen xl:h-screen w-screen bg-neutral-900 flex flex-col items-center justify-start p-2 md:p-8 pt-4 xl:pt-4 overflow-y-auto xl:overflow-hidden overflow-x-hidden relative">
      <AnimatePresence>
        {isLocked && <EmailGate onAccessGranted={handleAccessGranted} />}
      </AnimatePresence>

      {/* Top Actions */}
      <div className="w-full max-w-[1800px] flex justify-between items-center gap-3 mb-4 z-50">
        <div className="relative group px-1 w-[260px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 group-focus-within:text-blue-400 transition-colors" />
          <input
            type="text"
            placeholder="Search slides..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all shadow-inner"
          />
        </div>

        <div className="flex items-center gap-3">
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="bg-white/5 backdrop-blur-sm border-white/10 text-white hover:bg-white/10 hover:text-white"
              >
                I have questions
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-white border-0 shadow-2xl rounded-2xl p-0 overflow-hidden">
              <DialogHeader className="p-6 pb-2">
                <DialogTitle className="text-2xl font-bold text-slate-900 tracking-tight">
                  Get in Touch
                </DialogTitle>
                <DialogDescription className="text-slate-500 text-base">
                  Have questions about Asha? We're here to help.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3 p-6 pt-2">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-4 h-auto py-4 px-4 text-left border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 hover:shadow-md transition-all duration-200 group bg-white rounded-xl shadow-sm"
                  onClick={() =>
                    window.open(
                      "https://calendly.com/asha-health/dental-one-stop-shop",
                      "_blank"
                    )
                  }
                >
                  <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center text-blue-600 shrink-0 group-hover:scale-105 transition-transform duration-200 shadow-sm border border-blue-200">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="font-semibold text-slate-900 text-base group-hover:text-blue-700 transition-colors">
                      Book a meeting
                    </span>
                    <span className="text-sm text-slate-500">
                      Schedule time with our team
                    </span>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start gap-4 h-auto py-4 px-4 text-left border-slate-200 hover:border-green-300 hover:bg-green-50/30 hover:shadow-md transition-all duration-200 group bg-white rounded-xl shadow-sm"
                  onClick={() => (window.location.href = "sms:+19146779846")}
                >
                  <div className="w-12 h-12 rounded-2xl bg-green-100 flex items-center justify-center text-green-600 shrink-0 group-hover:scale-105 transition-transform duration-200 shadow-sm border border-green-200">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="font-semibold text-slate-900 text-base group-hover:text-green-700 transition-colors">
                      Text us
                    </span>
                    <span className="text-sm font-bold text-slate-700">
                      (914) 677-9846
                    </span>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start gap-4 h-auto py-4 px-4 text-left border-slate-200 hover:border-purple-300 hover:bg-purple-50/30 hover:shadow-md transition-all duration-200 group bg-white rounded-xl shadow-sm"
                  onClick={() =>
                    (window.location.href = "mailto:onboarding@asha.health")
                  }
                >
                  <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center text-purple-600 shrink-0 group-hover:scale-105 transition-transform duration-200 shadow-sm border border-purple-200">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="font-semibold text-slate-900 text-base group-hover:text-purple-700 transition-colors">
                      Email us
                    </span>
                    <span className="text-sm font-bold text-slate-700">
                      onboarding@asha.health
                    </span>
                  </div>
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20 font-semibold">
                I'm ready to get started
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-white border-0 shadow-2xl rounded-2xl p-0 overflow-hidden">
              <DialogHeader className="p-6 pb-2">
                <DialogTitle className="text-2xl font-bold text-slate-900 tracking-tight">
                  Complete your purchase order
                </DialogTitle>
                <DialogDescription className="text-slate-500 text-base">
                  Ready to transform your practice? Finalize your subscription
                  below.
                </DialogDescription>
              </DialogHeader>
              <div className="p-6 pt-2 flex flex-col gap-4">
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4 text-blue-600" />
                    </div>
                    <p className="text-sm text-blue-900 leading-relaxed">
                      Once you complete your purchase order, a member of our
                      team will reach out within 24 hours to schedule your
                      onboarding call.
                    </p>
                  </div>
                </div>

                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20 font-semibold h-12 text-lg rounded-xl"
                  onClick={() =>
                    window.open(
                      "https://buy.stripe.com/bJe6oI6FD9NV4Ku2z48AE0O",
                      "_blank"
                    )
                  }
                >
                  Complete Purchase <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="w-full max-w-[1800px] flex flex-col xl:flex-row gap-6 items-center justify-center flex-1 min-h-0">
        {/* Table of Contents - Left Side (Desktop) / Bottom (Mobile) */}
        <div className="w-full xl:w-[260px] flex flex-col flex-1 xl:flex-none xl:h-full py-4 xl:pt-0 xl:pb-8 pr-0 xl:pr-2 order-2 xl:order-1 min-h-[300px]">
          {/* Scrollable List */}
          <ScrollArea className="flex-1 -mr-2 pr-4 custom-scrollbar">
            <div className="pl-4 border-l border-white/10 space-y-2 py-2">
              {filteredSlides.map((slide) => (
                <button
                  key={slide.id}
                  onClick={() => api?.scrollTo(slide.originalIndex)}
                  className={`group flex items-center gap-3 w-full text-left transition-all duration-300 relative py-1.5 ${
                    current === slide.originalIndex + 1
                      ? "opacity-100"
                      : "opacity-40 hover:opacity-80"
                  }`}
                  style={{ paddingLeft: `${(slide.indent || 0) * 16}px` }}
                >
                  {/* Active Indicator Line */}
                  <div
                    className={`absolute -left-[17px] h-full w-[3px] rounded-full transition-all duration-500 ${
                      current === slide.originalIndex + 1
                        ? "bg-blue-500 opacity-100 shadow-[0_0_10px_rgba(59,130,246,0.5)]"
                        : "bg-transparent opacity-0"
                    }`}
                  />

                  <span
                    className={`text-sm font-medium tracking-wide transition-all duration-300 truncate ${
                      current === slide.originalIndex + 1
                        ? "text-blue-400 translate-x-1"
                        : "text-white"
                    }`}
                  >
                    {slide.title}
                  </span>
                </button>
              ))}
              {filteredSlides.length === 0 && (
                <div className="text-white/30 text-sm italic py-4">
                  No slides found
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Main Slide Container */}
        <div className="w-full xl:flex-1 max-w-[1400px] aspect-video relative order-1 xl:order-2 flex-shrink-0 group">
          <div
            ref={containerRef}
            className="w-full h-full overflow-hidden bg-transparent relative"
          >
            <div
              style={{
                width: "1400px",
                height: "787.5px", // 1400 * 9/16
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            >
              <Carousel setApi={setApi} className="w-full h-full">
                <div className="w-full h-full bg-white rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10 relative isolate transform-gpu">
                  <CarouselContent className="h-full ml-0">
                    {/* SLIDE 1: HERO */}
                    <CarouselItem className="h-full pl-0">
                      <div className="h-full w-full bg-gradient-to-br from-blue-600 to-indigo-600 flex flex-col items-center justify-center text-white px-4 relative overflow-hidden">
                        {/* Tech Grid Background - Increased visibility */}
                        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff15_1px,transparent_1px),linear-gradient(to_bottom,#ffffff15_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]" />

                        {/* Floating "Data" Particles - Restored */}
                        {[...Array(8)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="absolute w-1.5 h-1.5 bg-blue-200 rounded-full blur-[1px]"
                            initial={{
                              x: Math.random() * 1000,
                              y: Math.random() * 600,
                              opacity: 0,
                            }}
                            animate={{
                              y: [null, Math.random() * -100],
                              opacity: [0, 0.6, 0],
                            }}
                            transition={{
                              duration: Math.random() * 4 + 3,
                              repeat: Infinity,
                              delay: Math.random() * 2,
                            }}
                            style={{
                              left: `${Math.random() * 100}%`,
                              top: `${Math.random() * 100}%`,
                            }}
                          />
                        ))}

                        {/* Glowing Orbs - Restored with better blending */}
                        <motion.div
                          animate={{
                            y: [0, -30, 0],
                            scale: [1, 1.1, 1],
                            opacity: [0.3, 0.5, 0.3],
                          }}
                          transition={{
                            duration: 8,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                          className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-400 rounded-full blur-[120px] opacity-30 mix-blend-overlay"
                        />
                        <motion.div
                          animate={{
                            y: [0, 40, 0],
                            scale: [1, 1.2, 1],
                            opacity: [0.3, 0.6, 0.3],
                          }}
                          transition={{
                            duration: 10,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: 1,
                          }}
                          className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-indigo-400 rounded-full blur-[140px] opacity-30 mix-blend-overlay"
                        />

                        <motion.div
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                          className="text-center max-w-5xl z-10 relative"
                        >
                          <div className="mb-8 flex justify-center">
                            <div className="w-24 h-24 bg-white/10 backdrop-blur-md rounded-3xl flex items-center justify-center shadow-lg border border-white/20 relative group overflow-hidden p-4">
                              <img
                                src="/assets/alogo.png"
                                alt="Asha Logo"
                                className="w-full h-full object-contain relative z-10 drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]"
                              />
                              {/* Pulse effect behind logo - Restored */}
                              <div className="absolute inset-0 bg-white/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500 opacity-0 group-hover:opacity-100" />
                            </div>
                          </div>

                          {/* Smaller Text Size as requested */}
                          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 tracking-tighter drop-shadow-sm bg-clip-text text-transparent bg-gradient-to-b from-white via-white to-blue-200">
                            Asha Health
                          </h1>

                          <div className="relative inline-block mb-12">
                            <p className="text-lg md:text-xl lg:text-2xl font-light text-blue-50 max-w-4xl mx-auto leading-relaxed drop-shadow-sm">
                              The First{" "}
                              <span className="font-semibold text-white">
                                AI-Native Operating System
                              </span>{" "}
                              for Dental
                            </p>
                            {/* Underline effect - Restored */}
                            <motion.div
                              initial={{ scaleX: 0 }}
                              animate={{ scaleX: 1 }}
                              transition={{ delay: 0.8, duration: 0.8 }}
                              className="absolute -bottom-2 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-blue-200 to-transparent opacity-50"
                            />
                          </div>

                          <div className="flex justify-center gap-6">
                            <Button
                              onClick={() => api?.scrollNext()}
                              size="lg"
                              className="bg-white text-blue-600 hover:bg-blue-50 font-bold text-lg px-10 py-7 rounded-full shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.5)] transition-all transform hover:scale-105"
                            >
                              Start Tour <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                          </div>
                        </motion.div>
                      </div>
                    </CarouselItem>

                    {/* SLIDE 2: WHY ASHA */}
                    <CarouselItem className="h-full pl-0">
                      <div className="h-full w-full flex flex-row">
                        {/* Left Side: Blue */}
                        <div className="w-1/2 bg-blue-600 text-white flex flex-col justify-center p-20 relative overflow-hidden">
                          <div className="absolute top-0 left-0 w-64 h-64 bg-blue-500 rounded-full blur-3xl opacity-20 -translate-x-1/2 -translate-y-1/2" />
                          <div className="relative z-10">
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-12 leading-tight">
                              Why do practices choose to work with Asha Health?
                            </h2>

                            <div className="space-y-10">
                              <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1 }}
                              >
                                <h3 className="text-xl lg:text-2xl font-semibold mb-3 flex items-start gap-4">
                                  <span className="flex-shrink-0 bg-white/20 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mt-1">
                                    1
                                  </span>
                                  <span>We're the only 1-stop shop.</span>
                                </h3>
                                <p className="text-blue-100 leading-relaxed pl-12 text-lg opacity-90">
                                  Not just an AI receptionist. A full team of
                                  front office + back office AI agents. No need
                                  for 10 different vendors for isolated point
                                  solutions that don't connect.
                                </p>
                              </motion.div>

                              <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.3 }}
                              >
                                <h3 className="text-xl lg:text-2xl font-semibold mb-3 flex items-start gap-4">
                                  <span className="flex-shrink-0 bg-white/20 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mt-1">
                                    2
                                  </span>
                                  <span>Strongest backing in dental AI.</span>
                                </h3>
                                <p className="text-blue-100 leading-relaxed pl-12 text-lg opacity-90">
                                  Most amount of institutional capital raised,
                                  backed by Y-Combinator & General Catalyst.
                                  Built by Google AI product leaders & health
                                  system CMOs.
                                </p>
                              </motion.div>
                            </div>
                          </div>
                        </div>

                        {/* Right Side: Image */}
                        <div className="w-1/2 bg-slate-100 relative h-full">
                          <img
                            src="/assets/doctorimage1.jpg"
                            alt="Dental Professional"
                            className="w-full h-full object-cover object-center"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-12">
                            <div className="text-white">
                              <p className="font-medium text-xl mb-2">
                                Trusted by top practices
                              </p>
                              <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((i) => (
                                  <Star
                                    key={i}
                                    className="fill-yellow-400 text-yellow-400 w-6 h-6"
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CarouselItem>

                    {/* SLIDE 3: BACKING */}
                    <CarouselItem className="h-full pl-0">
                      <BackingSlide />
                    </CarouselItem>

                    {/* SLIDE 3.5: OFFERINGS */}
                    <CarouselItem className="h-full pl-0">
                      <OfferingsSlide
                        onNavigate={(type) => {
                          if (type === "front-office") {
                            // Navigate to Front Office Team (index 4 now)
                            api?.scrollTo(4);
                          } else if (type === "back-office") {
                            // Navigate to Back Office Team.
                            api?.scrollTo(26);
                          }
                        }}
                      />
                    </CarouselItem>

                    {/* SLIDE 4: FRONT OFFICE TEAM */}
                    <CarouselItem className="h-full pl-0">
                      <div className="h-full w-full bg-slate-100 flex flex-col p-8">
                        <div className="text-center mb-6 flex-shrink-0">
                          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-3">
                            <Sparkles className="w-4 h-4" />
                            <span>Digital Workforce</span>
                          </div>
                          <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                            A full team of AI staff to run your{" "}
                            <span className="text-blue-600">front office</span>.
                          </h2>
                        </div>

                        <div className="flex-grow flex items-center justify-center overflow-hidden">
                          <div className="grid grid-cols-4 gap-4 w-full max-w-[1300px]">
                            {frontOfficeAgents.map((agent, index) => (
                              <motion.div
                                key={agent.name}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="h-full"
                              >
                                <div
                                  className="group/card relative bg-white rounded-xl border border-slate-200 hover:border-blue-200 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col cursor-pointer h-[240px]"
                                  onClick={() => {
                                    if (agent.slug) {
                                      const slideIndex = slides.findIndex(
                                        (s) => s.slug === agent.slug
                                      );
                                      if (slideIndex !== -1) {
                                        api?.scrollTo(slideIndex);
                                      }
                                    }
                                  }}
                                >
                                  {/* Agent Header */}
                                  <div className="p-4 pb-0 flex items-start justify-between">
                                    <div className="relative">
                                      <div className="w-16 h-16 rounded-xl overflow-hidden shadow-sm ring-1 ring-slate-100">
                                        <img
                                          src={agent.image}
                                          alt={agent.name}
                                          className="w-full h-full object-cover transform group-hover/card:scale-110 transition-transform duration-500"
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                            e.target.parentNode.style.backgroundColor =
                                              "#f1f5f9";
                                          }}
                                        />
                                      </div>
                                      <div
                                        className="absolute -bottom-1 -right-1 bg-green-500 border-[2px] border-white w-4 h-4 rounded-full"
                                        title="Online"
                                      />
                                    </div>
                                    {agent.link && (
                                      <div className="w-6 h-6 rounded-full bg-slate-50 group-hover/card:bg-blue-600 flex items-center justify-center transition-colors duration-300">
                                        <ArrowRight className="w-3 h-3 text-slate-400 group-hover/card:text-white transition-colors" />
                                      </div>
                                    )}
                                  </div>

                                  {/* Content */}
                                  <div className="p-4 pt-3 flex-1 flex flex-col min-h-0">
                                    <div className="mb-2">
                                      <h3 className="text-lg font-bold text-slate-900 leading-tight">
                                        {agent.name},{" "}
                                        <span className="text-blue-600 font-bold">
                                          {agent.role}
                                        </span>
                                      </h3>
                                    </div>

                                    <p className="text-slate-600 text-xs leading-snug mb-3 line-clamp-3">
                                      {agent.description}
                                    </p>

                                    {/* Skills Tags */}
                                    <div className="flex flex-wrap gap-1 mt-auto">
                                      {agent.skills
                                        ?.slice(0, 2)
                                        .map((skill, i) => (
                                          <span
                                            key={i}
                                            className="px-1.5 py-0.5 rounded-md bg-slate-50 text-slate-600 text-[9px] font-bold border border-slate-100 uppercase tracking-wider"
                                          >
                                            {skill}
                                          </span>
                                        ))}
                                    </div>
                                  </div>

                                  {/* Hover highlight bar */}
                                  <div className="h-1 w-full bg-gradient-to-r from-blue-500 to-indigo-500 transform scale-x-0 group-hover/card:scale-x-100 transition-transform duration-300 origin-left" />
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CarouselItem>

                    {/* TITLE: AMY */}
                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide
                        name="Amy"
                        role="AI Dental Receptionist"
                        image="/assets/amy.png"
                        subtitle="Answers calls 24/7, and books patients directly into Asha PMS."
                      />
                    </CarouselItem>

                    {/* SLIDE 5: AI RECEPTIONIST DEMO */}
                    <CarouselItem className="h-full pl-0">
                      <AIReceptionistSlide />
                    </CarouselItem>

                    {/* SLIDE 6: INTELLIGENT SCHEDULING */}
                    <CarouselItem className="h-full pl-0">
                      <IntelligentSchedulingSlide />
                    </CarouselItem>

                    {/* TITLE: LEENA */}
                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide
                        name="Leena"
                        role="Schedule Optimizer"
                        image="/assets/leena.png"
                        subtitle="The intelligent agent who proactively keeps your chairs full and production high."
                      />
                    </CarouselItem>

                    {/* SLIDE 7: LENA - SCHEDULE OPTIMIZER */}
                    <CarouselItem className="h-full pl-0">
                      <LenaSlide />
                    </CarouselItem>

                    {/* SLIDE 8: LENA - WORKFLOW */}
                    <CarouselItem className="h-full pl-0">
                      <LenaWorkflowSlide />
                    </CarouselItem>

                    {/* TITLE: STEPHANIE */}
                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide
                        name="Stephanie"
                        role="Hygiene Recall Specialist"
                        image="/assets/stephanie.png"
                        subtitle="Runs intelligent AI voice campaigns to reactivate patients and fill your hygiene schedule."
                      />
                    </CarouselItem>

                    {/* SLIDE 8: STEPHANIE - HYGIENE RECALL */}
                    <CarouselItem className="h-full pl-0">
                      <HygieneRecallSlide />
                    </CarouselItem>

                    {/* TITLE: KIM */}
                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide
                        name="Kim"
                        role="Case Acceptance Specialist"
                        image="/assets/kim.png"
                        subtitle="Turning unscheduled treatment plans into booked appointments and revenue."
                      />
                    </CarouselItem>

                    {/* SLIDE: KIM - CASE ACCEPTANCE */}
                    <CarouselItem className="h-full pl-0">
                      <KimCaseAcceptanceSlide />
                    </CarouselItem>

                    {/* TITLE: ELYSE */}
                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide
                        name="Elise"
                        role="Patient Experience Coordinator"
                        image="/assets/elise.png"
                        subtitle="Ensuring every patient feels heard and turning happy visits into 5-star reviews."
                      />
                    </CarouselItem>

                    {/* SLIDE: ELYSE - PATIENT EXPERIENCE */}
                    <CarouselItem className="h-full pl-0">
                      <ElisePatientExperienceSlide />
                    </CarouselItem>

                    {/* TITLE: MORGAN */}
                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide
                        name="Morgan"
                        role="Patient Intake Specialist"
                        image="/assets/morgan.png"
                        subtitle="Ensures patients complete intake forms and provide insurance info before they arrive."
                      />
                    </CarouselItem>

                    {/* SLIDE: MORGAN - PATIENT INTAKE */}
                    <CarouselItem className="h-full pl-0">
                      <MorganPatientIntakeSlide />
                    </CarouselItem>

                    {/* TITLE: TRISHA */}
                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide
                        name="Trisha"
                        role="AI Practice Manager"
                        image="/assets/trisha.png"
                        subtitle="Monitors your entire AI workforce and assigns human follow-up only when necessary."
                      />
                    </CarouselItem>

                    {/* SLIDE: PRACTICE MANAGER DASHBOARD */}
                    <CarouselItem className="h-full pl-0">
                      <PracticeManagerSlide />
                    </CarouselItem>

                    {/* SLIDE 12: COMMUNICATION SUITE TITLE */}
                    <CarouselItem className="h-full pl-0">
                      <CommunicationTitleSlide />
                    </CarouselItem>

                    {/* SLIDE 13: VOIP */}
                    <CarouselItem className="h-full pl-0">
                      <VoIPSlide />
                    </CarouselItem>

                    {/* NEW SLIDE: NEXT-GEN FORMS */}
                    <CarouselItem className="h-full pl-0">
                      <NextGenFormsSlide />
                    </CarouselItem>

                    {/* SLIDE 14: 2-WAY MESSAGING */}
                    <CarouselItem className="h-full pl-0">
                      <CommunicationSuiteSlide />
                    </CarouselItem>

                    {/* SLIDE 15: EFAX */}
                    <CarouselItem className="h-full pl-0">
                      <EFAXSlide />
                    </CarouselItem>

                    {/* NEW SLIDE: SMS CAMPAIGNS */}
                    <CarouselItem className="h-full pl-0">
                      <SMSCampaignsSlide />
                    </CarouselItem>

                    {/* NEW SLIDE: EMAIL CAMPAIGNS */}
                    <CarouselItem className="h-full pl-0">
                      <EmailCampaignsSlide />
                    </CarouselItem>



                    {/* SLIDE 14: BACK OFFICE TEAM */}
                    <CarouselItem className="h-full pl-0">
                      <div className="h-full w-full bg-slate-100 flex flex-col p-8">
                        <div className="text-center mb-6 flex-shrink-0">
                          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-3">
                            <Sparkles className="w-4 h-4" />
                            <span>Digital Workforce</span>
                          </div>
                          <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                            A full team of AI staff to run your{" "}
                            <span className="text-blue-600">back office</span>.
                          </h2>
                        </div>

                        <div className="flex-grow flex items-center justify-center overflow-hidden">
                          <div className="grid grid-cols-4 gap-4 w-full max-w-[1300px]">
                            {backOfficeAgents.map((agent, index) => (
                              <motion.div
                                key={agent.name}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="h-full"
                              >
                                <div
                                  className="group/card relative bg-white rounded-xl border border-slate-200 hover:border-blue-200 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col cursor-pointer h-[240px]"
                                  onClick={() => {
                                    const slideMap = {
                                      Amanda: 13,
                                      Ariel: 34,
                                      Alayna: 36,
                                      Alisha: 37,
                                      Kianna: 41,
                                      Sarah: 36,
                                      Julia: 41,
                                      Laura: 18,
                                    };
                                    if (slideMap[agent.name] !== undefined) {
                                      api?.scrollTo(slideMap[agent.name]);
                                    }
                                  }}
                                >
                                  {/* Agent Header */}
                                  <div className="p-4 pb-0 flex items-start justify-between">
                                    <div className="relative">
                                      <div className="w-16 h-16 rounded-xl overflow-hidden shadow-sm ring-1 ring-slate-100">
                                        <img
                                          src={agent.image}
                                          alt={agent.name}
                                          className="w-full h-full object-cover transform group-hover/card:scale-110 transition-transform duration-500"
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                            e.target.parentNode.style.backgroundColor =
                                              "#f1f5f9";
                                          }}
                                        />
                                      </div>
                                      <div
                                        className="absolute -bottom-1 -right-1 bg-green-500 border-[2px] border-white w-4 h-4 rounded-full"
                                        title="Online"
                                      />
                                    </div>
                                    {agent.link && (
                                      <div className="w-6 h-6 rounded-full bg-slate-50 group-hover/card:bg-blue-600 flex items-center justify-center transition-colors duration-300">
                                        <ArrowRight className="w-3 h-3 text-slate-400 group-hover/card:text-white transition-colors" />
                                      </div>
                                    )}
                                  </div>

                                  {/* Content */}
                                  <div className="p-4 pt-3 flex-1 flex flex-col min-h-0">
                                    <div className="mb-2">
                                      <h3 className="text-lg font-bold text-slate-900 leading-tight">
                                        {agent.name},{" "}
                                        <span className="text-blue-600 font-bold">
                                          {agent.role}
                                        </span>
                                      </h3>
                                    </div>

                                    <p className="text-slate-600 text-xs leading-snug mb-3 line-clamp-3">
                                      {agent.description}
                                    </p>

                                    {/* Skills Tags */}
                                    <div className="flex flex-wrap gap-1 mt-auto">
                                      {agent.skills
                                        ?.slice(0, 2)
                                        .map((skill, i) => (
                                          <span
                                            key={i}
                                            className="px-1.5 py-0.5 rounded-md bg-slate-50 text-slate-600 text-[9px] font-bold border border-slate-100 uppercase tracking-wider"
                                          >
                                            {skill}
                                          </span>
                                        ))}
                                    </div>
                                  </div>

                                  {/* Hover highlight bar */}
                                  <div className="h-1 w-full bg-gradient-to-r from-blue-500 to-indigo-500 transform scale-x-0 group-hover/card:scale-x-100 transition-transform duration-300 origin-left" />
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CarouselItem>

                    {/* TITLE: AMANDA */}
                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide
                        name="Amanda"
                        role="Insurance Verification Specialist"
                        image="/assets/amanda.png"
                        subtitle="The only AI that performs dual-source insurance verification to get you more accurate and comprehensive benefits breakdowns."
                      />
                    </CarouselItem>

                    {/* SLIDE 9: INSURANCE VERIFICATION */}
                    <CarouselItem className="h-full pl-0">
                      <InsuranceVerificationSlide />
                    </CarouselItem>

                    {/* NEW SLIDE: COMPREHENSIVE BREAKDOWNS */}
                    <CarouselItem className="h-full pl-0">
                      <ComprehensiveBreakdownsSlide />
                    </CarouselItem>

                    {/* NEW SLIDE: AI INSURANCE CO-PILOT */}
                    <CarouselItem className="h-full pl-0">
                      <InsuranceCopilotSlide isActive={current === (slides.findIndex(s => s.slug === "amanda-copilot") + 1)} />
                    </CarouselItem>

                    {/* SLIDE 11: BENEFIT FORMS */}
                    <CarouselItem className="h-full pl-0">
                      <BenefitFormsSlide />
                    </CarouselItem>

                    {/* SLIDE 10: AI INSURANCE CALLING (Moved from Laura Section) */}
                    <CarouselItem className="h-full pl-0">
                      <InsuranceCallingSlide />
                    </CarouselItem>

                    {/* TITLE: ARIEL */}
                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide
                        name="Ariel"
                        role="AI Claims Expert"
                        image="/assets/ariel.png"
                        subtitle="Submitting clean claims with perfect clinical documentation instantly."
                        theme="dark"
                      />
                    </CarouselItem>

                    {/* SLIDE 15: CLAIM SUBMISSION (ARIEL) */}
                    <CarouselItem className="h-full pl-0">
                      <ArielSlide />
                    </CarouselItem>

                    {/* NEW SLIDE: CLAIM SCRUBBING (ALAINA) */}
                    <CarouselItem className="h-full pl-0">
                      <AlainaSlide />
                    </CarouselItem>

                    {/* TITLE: ALISHA */}
                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide
                        name="Alisha"
                        role="EOB Posting Specialist"
                        image="/assets/alisha.png"
                        subtitle="Posts explanation of benefits and insurance payments automatically."
                        theme="dark"
                      />
                    </CarouselItem>

                    {/* SLIDE 16: EOB POSTING (PROCESSING) */}
                    <CarouselItem className="h-full pl-0">
                      <EOBProcessingSlide />
                    </CarouselItem>

                    {/* NEW SLIDE: PAYMENT RECONCILIATION */}
                    <CarouselItem className="h-full pl-0">
                      <PaymentReconciliationSlide />
                    </CarouselItem>

                    {/* NEW SLIDE: EOB STREAMLINING */}
                    <CarouselItem className="h-full pl-0">
                      <EOBStreamliningSlide />
                    </CarouselItem>

                    {/* TITLE: KIANNA */}
                    <CarouselItem className="h-full pl-0">
                      <AgentTitleSlide
                        name="Kianna"
                        role="Patient Billing"
                        image="/assets/kianna.png"
                        subtitle="Uses intelligent voice AI campaigns to help patients pay their balances."
                        theme="dark"
                      />
                    </CarouselItem>

                    {/* NEW SLIDE: PATIENT BILLING / VOICE CAMPAIGNS */}
                    <CarouselItem className="h-full pl-0">
                      <PatientBillingSlide />
                    </CarouselItem>

                    {/* SLIDE 17: HUMAN BILLER */}
                    <CarouselItem className="h-full pl-0">
                      <HumanBillerSlide />
                    </CarouselItem>


                    {/* PRICING SECTION TITLE */}
                    <CarouselItem className="h-full pl-0">
                      <div className="h-full w-full bg-slate-900 flex flex-col items-center justify-center text-white p-8">
                        <div className="text-center space-y-6 max-w-4xl">
                           <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-emerald-500/10 text-emerald-500 mb-4 border border-emerald-500/20">
                             <span className="text-4xl font-bold">$</span>
                           </div>
                           <h1 className="text-5xl md:text-7xl font-bold tracking-tight">
                             Pricing & <span className="text-emerald-400">Packaging</span>
                           </h1>
                           <p className="text-xl md:text-2xl text-slate-400 max-w-2xl mx-auto">
                             Simple, transparent pricing for your AI workforce.
                           </p>
                        </div>
                      </div>
                    </CarouselItem>

                    {/* NEW SLIDE: COMPETITOR ANALYSIS (FRONT OFFICE) */}
                    <CarouselItem className="h-full pl-0">
                      <CompetitorAnalysisSlide />
                    </CarouselItem>

                    {/* NEW SLIDE: COMMUNICATION SUITE COMPARISON */}
                    <CarouselItem className="h-full pl-0">
                      <CommunicationSuiteComparisonSlide />
                    </CarouselItem>

                    {/* NEW SLIDE: BACK OFFICE COMPETITOR ANALYSIS */}
                    <CarouselItem className="h-full pl-0">
                      <BackOfficeCompetitorAnalysisSlide />
                    </CarouselItem>

                    {/* NEW SLIDE: TOTAL PRICING COMPARISON */}
                    <CarouselItem className="h-full pl-0">
                      <TotalPricingComparisonSlide />
                    </CarouselItem>

                    {/* NEW SLIDE: ASHA OPERATING SYSTEM */}
                    <CarouselItem className="h-full pl-0">
                      <AshaOperatingSystemSlide />
                    </CarouselItem>

                    {/* GETTING STARTED SECTION TITLE */}
                    <CarouselItem className="h-full pl-0">
                      <div className="h-full w-full bg-slate-900 flex flex-col items-center justify-center text-white p-8">
                        <div className="text-center space-y-6 max-w-4xl">
                           <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-blue-500/10 text-blue-500 mb-4 border border-blue-500/20">
                             <Sparkles className="w-10 h-10" />
                           </div>
                           <h1 className="text-5xl md:text-7xl font-bold tracking-tight">
                             Getting <span className="text-blue-400">Started</span>
                           </h1>
                           <p className="text-xl md:text-2xl text-slate-400 max-w-2xl mx-auto">
                             Simple steps to transform your practice today.
                           </p>
                        </div>
                      </div>
                    </CarouselItem>

                    {/* NEW SLIDE: GETTING STARTED (FRONT OFFICE) */}
                    <CarouselItem className="h-full pl-0">
                      <FrontOfficeGettingStartedSlide />
                    </CarouselItem>

                    {/* NEW SLIDE: GETTING STARTED (BACK OFFICE) */}
                    <CarouselItem className="h-full pl-0">
                      <GettingStartedSlide />
                    </CarouselItem>

                    {/* NEW SLIDE: WHITE GLOVE ONBOARDING */}
                    <CarouselItem className="h-full pl-0">
                      <WhiteGloveOnboardingSlide />
                    </CarouselItem>

                    {/* NEW SLIDE: CONTRACT TERMS */}
                    <CarouselItem className="h-full pl-0">
                      <ContractTermsSlide />
                    </CarouselItem>

                    {/* FAQ SECTION TITLE */}
                    <CarouselItem className="h-full pl-0">
                      <div className="h-full w-full bg-slate-900 flex flex-col items-center justify-center text-white p-8">
                        <div className="text-center space-y-6 max-w-4xl">
                           <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-blue-500/10 text-blue-500 mb-4 border border-blue-500/20">
                             <Sparkles className="w-10 h-10" />
                           </div>
                           <h1 className="text-5xl md:text-7xl font-bold tracking-tight">
                             Common <span className="text-blue-400">Questions</span>
                           </h1>
                           <p className="text-xl md:text-2xl text-slate-400 max-w-2xl mx-auto">
                             Everything you need to know about partnering with Asha.
                           </p>
                        </div>
                      </div>
                    </CarouselItem>

                    {/* NEW SLIDE: FAQ */}
                    <CarouselItem className="h-full pl-0">
                      <FAQSlide />
                    </CarouselItem>

                    {/* ROI INTRO SLIDE */}
                    <CarouselItem className="h-full pl-0">
                      <ROIIntroSlide />
                    </CarouselItem>

                    {/* NTX MENTAL HEALTH CASE STUDY */}
                    <CarouselItem className="h-full pl-0">
                      <CaseStudySlide
                        title="How Asha transformed"
                        titleHighlight="NTX Mental Health"
                        image="/assets/casestudyimage2.png"
                        stats={[
                          { label: "EHR", value: "PracticeQ" },
                          { label: "Num Providers", value: "20+" },
                          {
                            label: "Specialties",
                            value: "Psychiatry, Therapy, Mental Health",
                          },
                        ]}
                        description="Asha developed a full team of AI agents hyper-specialized for NTX's practice: AI receptionist, AI patient intake agent, AI scribe, AI SMS agent, AI schedule optimizer, AI patient experience agent, AI insurance verification agent, and more."
                        roiCards={[
                          {
                            label: "Calls fully automated",
                            value: "67%",
                            subtext: "no human intervention",
                          },
                          {
                            label: "Note-taking time eliminated",
                            value: "82%",
                            subtext: "with our AI scribe",
                          },
                        ]}
                      />
                    </CarouselItem>

                    {/* PROCARE CASE STUDY */}
                    <CarouselItem className="h-full pl-0">
                      <CaseStudySlide
                        title="How Asha drove tangible ROI for"
                        titleHighlight="Procare Dentistry"
                        image="/assets/practicelogos/image 1.png"
                        imageType="logo"
                        stats={[
                          { label: "PMS", value: "Dentrix (Server)" },
                          { label: "Period", value: "3 Weeks" },
                          { label: "Tasks", value: "1,420" },
                        ]}
                        description="Asha's AI suite automated 50 receptionist calls, 134 insurance verifications, and over 1,000 patient communications. In the first 3 weeks alone, we secured 4 new patients and reactivated 2 recalls, directly generating $6,600 in increased production."
                        roiCards={[
                          {
                            label: "Total ROI",
                            value: "$8,146",
                            subtext: "in just 3 weeks",
                          },
                          {
                            label: "Production Increase",
                            value: "$6,600",
                            subtext: "from recalls & bookings",
                          },
                          {
                            label: "New Patients",
                            value: "4",
                            subtext: "automatically booked",
                          },
                          {
                            label: "Appointments Saved",
                            value: "18",
                            subtext: "filled, recalled & confirmed",
                          },
                        ]}
                      />
                    </CarouselItem>

                    {/* ADVANCED DENTAL HEALTH CASE STUDY */}
                    <CarouselItem className="h-full pl-0">
                      <CaseStudySlide
                        title="How Asha drove tangible ROI for"
                        titleHighlight="Advanced Dental Health"
                        image="/assets/practicelogos/advanceddentalhealth.png"
                        imageType="logo"
                        logoScale={0.8}
                        stats={[
                          { label: "PMS", value: "Dentrix" },
                          { label: "Migrated From", value: "Weave & Airpay" },
                          { label: "Tasks", value: "1,744" },
                        ]}
                        description="This 2-doctor practice switched to Asha to consolidate their tech stack. Our agents handled 242 insurance calls (saving 1,363 mins of hold time), while our receptionist and recall agents booked 6 new patients and 8 hygiene appointments."
                        roiCards={[
                          {
                            label: "Total ROI",
                            value: "$12,934",
                            subtext: "production + labor savings",
                          },
                          {
                            label: "Production Increase",
                            value: "$10,650",
                            subtext: "from new appts",
                          },
                          {
                            label: "Insurance Calls",
                            value: "242",
                            subtext: "fully automated",
                          },
                          {
                            label: "Hygiene Recalls",
                            value: "8",
                            subtext: "patients reactivated",
                          },
                        ]}
                      />
                    </CarouselItem>

                    {/* ROWLAND FAMILY DENTISTRY CASE STUDY */}
                    <CarouselItem className="h-full pl-0">
                      <CaseStudySlide
                        title="How Asha drove tangible ROI for"
                        titleHighlight="Rowland Family Dentistry"
                        image="/assets/practiceimages/rowland.webp"
                        stats={[
                          { label: "PMS", value: "Eaglesoft" },
                          { label: "Tasks", value: "3,014" },
                          { label: "Verifications", value: "1,225" },
                        ]}
                        description="Asha's AI Receptionist handled 376 calls and booked 54 new patients, while our Insurance agent processed 1,225 verifications. The combined impact of our front-office suite delivered over $65k in value."
                        roiCards={[
                          {
                            label: "Total ROI",
                            value: "$65,398",
                            subtext: "comprehensive impact",
                          },
                          {
                            label: "Production Increase",
                            value: "$59,850",
                            subtext: "from booked appts",
                          },
                          {
                            label: "New Patients",
                            value: "54",
                            subtext: "automatically booked",
                          },
                          {
                            label: "Verifications",
                            value: "1,225",
                            subtext: "completed automatically",
                          },
                        ]}
                      />
                    </CarouselItem>

                    {/* ALPHA DENTAL EXCELLENCE CASE STUDY - HIDDEN */}
                    {/* 
                    <CarouselItem className="h-full pl-0">
                      <CaseStudySlide
                        title="How Asha drove tangible ROI for"
                        titleHighlight="Alpha Dental Excellence"
                        image="/assets/practiceimages/alphadental.webp"
                        stats={[
                          { label: "PMS", value: "Denticon" },
                          { label: "Staff", value: "4 Dentists, 1 Hygienist" },
                          { label: "Tasks", value: "3,060" },
                        ]}
                        description="Handling a high volume of 626 calls and over 1,000 schedule optimizer communications, Asha's agents secured 55 new patients and processed 573 insurance verifications, saving 518 minutes of staff phone time."
                        roiCards={[
                          {
                            label: "Total ROI",
                            value: "$73,066",
                            subtext: "production + labor savings",
                          },
                          {
                            label: "Production Increase",
                            value: "$68,750",
                            subtext: "from 132 booked appts",
                          },
                          {
                            label: "New Patients",
                            value: "55",
                            subtext: "automatically booked",
                          },
                          {
                            label: "Verifications",
                            value: "573",
                            subtext: "processed automatically",
                          },
                        ]}
                      />
                    </CarouselItem>
                    */}

                    {/* REAL ROI SLIDE */}
                    <CarouselItem className="h-full pl-0">
                      <RealROISlide />
                    </CarouselItem>

                    {/* COMPARISONS SECTION */}
                    <CarouselItem className="h-full pl-0">
                      <ComparisonsTitleSlide />
                    </CarouselItem>

                    {/* ASHA VS DENTINA: PLATFORM */}
                    <CarouselItem className="h-full pl-0">
                      <AshaVsDentinaSlide />
                    </CarouselItem>

                    {/* ASHA VS DENTINA: RECEPTIONIST */}
                    <CarouselItem className="h-full pl-0">
                      <AshaVsDentinaReceptionistSlide />
                    </CarouselItem>

                    {/* ASHA VS WEAVE */}
                    <CarouselItem className="h-full pl-0">
                      <AshaVsWeaveSlide />
                    </CarouselItem>

                    {/* ASHA VS LASSIE */}
                    <CarouselItem className="h-full pl-0">
                      <AshaVsLassieSlide />
                    </CarouselItem>
                  </CarouselContent>
                </div>
              </Carousel>
            </div>
          </div>

          <Button
            variant="outline"
            size="icon"
            className="absolute -left-6 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 border-0 text-slate-800 hover:text-slate-900 h-12 w-12 rounded-full backdrop-blur-sm shadow-sm z-50 disabled:opacity-50"
            onClick={() => api?.scrollPrev()}
            disabled={!api || current <= 1}
          >
            <ArrowLeft className="h-6 w-6" />
            <span className="sr-only">Previous slide</span>
          </Button>

          <Button
            variant="outline"
            size="icon"
            className="absolute -right-6 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 border-0 text-slate-800 hover:text-slate-900 h-12 w-12 rounded-full backdrop-blur-sm shadow-sm z-50 disabled:opacity-50"
            onClick={() => api?.scrollNext()}
            disabled={!api || current >= count}
          >
            <ArrowRight className="h-6 w-6" />
            <span className="sr-only">Next slide</span>
          </Button>
        </div>
      </div>
      {/* Mobile Alert */}
      <div className="fixed bottom-0 left-0 right-0 bg-blue-600/90 backdrop-blur-md text-white text-center py-3 px-4 text-xs font-semibold xl:hidden z-50 border-t border-blue-400/30 shadow-lg">
        This slideshow is designed best to be viewed on a laptop.
      </div>
    </div>
  );
};

const frontOfficeAgents = [
  {
    name: "Amy",
    slug: "amy",
    role: "Receptionist",
    description:
      "Handles inbound calls 24/7 with zero patient wait times. Schedules directly into Asha PMS.",
    image: "/assets/amy.png",
    link: "/agents/receptionist",
    skills: ["Inbound Calls", "Scheduling", "Q&A"],
  },
  {
    name: "Leena",
    slug: "leena",
    role: "Schedule Optimizer",
    description:
      "Prevents no-shows and fills cancellations with new patients to maximize production.",
    image: "/assets/leena.png",
    link: "/agents/schedule-optimizer",
    skills: ["Schedule Filling", "Confirmation", "Recall"],
  },
  {
    name: "Stephanie",
    slug: "stephanie",
    role: "Hygiene Recall",
    description:
      "Proactively calls unscheduled patients to book their hygiene appointments.",
    image: "/assets/stephanie.png",
    link: "/agents/hygiene-recall",
    skills: ["Outbound Calls", "Reactivation", "Booking"],
  },
  {
    name: "Elise",
    slug: "elise",
    role: "Patient Experience",
    description:
      "Follows up with patients to ensure satisfaction and generate 5-star reviews.",
    image: "/assets/elise.png",
    skills: ["Feedback", "Reviews", "Follow-up"],
  },
  {
    name: "Kim",
    slug: "kim",
    role: "Case Acceptance",
    description:
      "Helps patients understand treatment plans and move forward with care.",
    image: "/assets/kim.png",
    skills: ["Treatment Pres.", "Education", "Sales"],
  },
  {
    name: "Morgan",
    slug: "morgan",
    role: "Patient Intake",
    description:
      "Ensures patients complete intake forms and provide insurance info before they arrive.",
    image: "/assets/morgan.png",
    skills: ["Forms", "Insurance", "Intake"],
  },
  {
    name: "Trisha",
    slug: "practice-manager",
    role: "Practice Manager",
    description:
      "Monitors all AI agents, assigns tasks to staff, and ensures nothing falls through the cracks.",
    image: "/assets/trisha.png",
    skills: ["Task Assignment", "Monitoring", "Accountability"],
  },
  {
    name: "Joseph",
    slug: "fax",
    role: "Fax Management",
    description:
      "Auto-sorts incoming faxes and attaches them to patient files in Asha PMS.",
    image: "/assets/joseph.png",
    skills: ["Sorting", "Filing", "PMS Sync"],
  },
];

const backOfficeAgents = [
  {
    name: "Amanda",
    role: "Insurance Verification",
    description:
      "Automatically verifies patient insurance benefits and eligibility hours before visits.",
    image: "/assets/amanda.png",
    link: "/agents/insurance-verification",
    skills: ["Verification", "Benefits", "Eligibility"],
  },
  {
    name: "Ariel",
    role: "Claim Submission",
    description:
      "Generates and submits clean insurance claims to payers instantly.",
    image: "/assets/ariel.png",
    skills: ["Claims", "Submission", "Coding"],
  },
  {
    name: "Alayna",
    role: "Claim Scrubbing",
    description:
      "Reviews and corrects claims before submission to reduce denials.",
    image:
      "https://framerusercontent.com/images/gpvxkPXtZHSDq7LbDrvpyEwKzAk.png",
    skills: ["Auditing", "Corrections", "Compliance"],
  },
  {
    name: "Sarah",
    role: "Denial Management",
    description:
      "Handles denied claims, appeals, and resubmissions automatically.",
    image: "/assets/sarah.png",
    skills: ["Appeals", "Resubmission", "Negotiation"],
  },
  {
    name: "Kianna",
    role: "Patient Billing",
    description:
      "Manages patient statements and answers billing questions via text/phone.",
    image: "/assets/kianna.png",
    skills: ["Statements", "Payments", "Inquiries"],
  },
  {
    name: "Julia",
    role: "AR & Collections",
    description:
      "Tracks accounts receivable and manages collection activities.",
    image: "/assets/julia.png",
    skills: ["Collections", "Tracking", "Outreach"],
  },
  {
    name: "Alisha",
    role: "EOB Posting",
    description:
      "Posts explanation of benefits and insurance payments automatically.",
    image: "/assets/alisha.png",
    skills: ["Posting", "Reconciliation", "Data Entry"],
  },
  {
    name: "Laura",
    role: "Insurance Calls",
    description:
      "Handles insurance-related phone calls and inquiries efficiently.",
    image:
      "https://framerusercontent.com/images/8s85NGEzWU5LVKALaMlopNKSHg.png",
    skills: ["Inbound Calls", "Inquiries", "Support"],
  },
];

export default SlideshowPage;
